import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  IconButton,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import BaseContainer from '../GameContainer/BaseContainer.tsx';
import BaseHeader from '../GameHeader/BaseHeader.tsx';
import ArchiveCalendar from './ArchiveCalendar.tsx';
import { GameType } from '../../types/GameType.ts';
import { getGameConfig } from '../../types/GameConfig.ts';
import { getArchiveStartDate } from '../../utils/archiveUtils.ts';
import './ArchivePage.css';

interface ArchivePageProps {
  gameType: GameType;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function ArchivePage(props: ArchivePageProps) {
  const { gameType } = props;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const gameConfig = getGameConfig(gameType);
  const archiveStart = getArchiveStartDate(gameType);

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  // Read initial year/month from URL search params, defaulting to current month
  const paramYear = searchParams.get('year');
  const paramMonth = searchParams.get('month');
  const initialYear = paramYear ? parseInt(paramYear, 10) : currentYear;
  const initialMonth = paramMonth ? parseInt(paramMonth, 10) : currentMonth;

  const [selectedYear, setSelectedYear] = useState(initialYear);
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);

  // Compute valid year range
  const startYear = archiveStart.year;
  const endYear = currentYear;
  const years: number[] = [];
  for (let y = startYear; y <= endYear; y++) {
    years.push(y);
  }

  // Compute valid months for the selected year
  const getValidMonths = (year: number): number[] => {
    const months: number[] = [];
    const startMonth = year === startYear ? archiveStart.month : 0;
    const endMonth = year === currentYear ? currentMonth : 11;
    for (let m = startMonth; m <= endMonth; m++) {
      months.push(m);
    }
    return months;
  };

  const validMonths = getValidMonths(selectedYear);

  const canGoPrev = selectedYear > startYear || selectedMonth > archiveStart.month;
  const canGoNext = selectedYear < currentYear || selectedMonth < currentMonth;

  const updateParams = (year: number, month: number) => {
    setSearchParams({ year: year.toString(), month: month.toString() }, { replace: true });
  };

  const handlePrevMonth = () => {
    if (!canGoPrev) return;
    let newYear = selectedYear;
    let newMonth = selectedMonth;
    if (selectedMonth === 0) {
      newYear = selectedYear - 1;
      newMonth = 11;
    } else {
      newMonth = selectedMonth - 1;
    }
    setSelectedYear(newYear);
    setSelectedMonth(newMonth);
    updateParams(newYear, newMonth);
  };

  const handleNextMonth = () => {
    if (!canGoNext) return;
    let newYear = selectedYear;
    let newMonth = selectedMonth;
    if (selectedMonth === 11) {
      newYear = selectedYear + 1;
      newMonth = 0;
    } else {
      newMonth = selectedMonth + 1;
    }
    setSelectedYear(newYear);
    setSelectedMonth(newMonth);
    updateParams(newYear, newMonth);
  };

  const handleMonthChange = (event: SelectChangeEvent<number>) => {
    const newMonth = event.target.value as number;
    setSelectedMonth(newMonth);
    updateParams(selectedYear, newMonth);
  };

  const handleYearChange = (event: SelectChangeEvent<number>) => {
    const newYear = event.target.value as number;
    setSelectedYear(newYear);
    const newValidMonths = getValidMonths(newYear);
    let newMonth = selectedMonth;
    if (!newValidMonths.includes(selectedMonth)) {
      newMonth = newValidMonths[newValidMonths.length - 1];
      setSelectedMonth(newMonth);
    }
    updateParams(newYear, newMonth);
  };

  const headerRightContent = (
    <button className='iconButton' onClick={() => navigate(gameConfig.routePath)}>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <path d="M 12 2.0996094 L 1 12 L 4 12 L 4 21 L 11 21 L 11 15 L 13 15 L 13 21 L 20 21 L 20 12 L 23 12 L 12 2.0996094 z M 12 4.7910156 L 18 10.191406 L 18 11 L 18 19 L 15 19 L 15 13 L 9 13 L 9 19 L 6 19 L 6 10.191406 L 12 4.7910156 z" />
      </svg>
    </button>
  );

  const header = (
    <BaseHeader
      title={`${gameConfig.name} Archive`}
      rightContent={headerRightContent}
    />
  );

  return (
    <BaseContainer header={header}>
      <div className="archive-page">
        <div className="archive-nav">
          <IconButton
            onClick={handlePrevMonth}
            disabled={!canGoPrev}
            size="small"
            sx={{ border: '1px solid #ccc', borderRadius: '50%' }}
          >
            <ChevronLeft />
          </IconButton>

          <Select
            value={selectedMonth}
            onChange={handleMonthChange}
            size="small"
            variant="outlined"
            sx={{ minWidth: 120 }}
          >
            {validMonths.map(m => (
              <MenuItem key={m} value={m}>{MONTH_NAMES[m]}</MenuItem>
            ))}
          </Select>

          <Select
            value={selectedYear}
            onChange={handleYearChange}
            size="small"
            variant="outlined"
            sx={{ minWidth: 80 }}
          >
            {years.map(y => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>

          <IconButton
            onClick={handleNextMonth}
            disabled={!canGoNext}
            size="small"
            sx={{ border: '1px solid #ccc', borderRadius: '50%' }}
          >
            <ChevronRight />
          </IconButton>
        </div>

        <ArchiveCalendar
          gameType={gameType}
          year={selectedYear}
          month={selectedMonth}
        />
      </div>
    </BaseContainer>
  );
}
